// RepairTypeStep - Repair type selection for worksheet wizard
// 3 types: Garanciális (ML-G), Javításra (ML-J), Szerződéses (ML-J-P)
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import type { RepairType } from '../wizard-store';
import { useWorksheetWizardStore } from '../wizard-store';

interface RepairTypeOption {
  type: RepairType;
  labelKey: string;
  descriptionKey: string;
  code: string;
  color: {
    ring: string;
    bg: string;
    icon: string;
    badge: string;
    badgeText: string;
  };
  icon: 'shield' | 'wrench' | 'handshake';
}

const REPAIR_TYPE_OPTIONS: RepairTypeOption[] = [
  {
    type: 'GARANCIALIS',
    labelKey: 'worksheet.repairType.garancialis',
    descriptionKey: 'worksheet.repairType.garancialisDesc',
    code: 'ML-G',
    color: {
      ring: 'ring-green-500 bg-green-50 dark:bg-green-900/20',
      bg: 'bg-green-100 dark:bg-green-900/30',
      icon: 'text-green-600 dark:text-green-400',
      badge: 'bg-green-100 dark:bg-green-900/30',
      badgeText: 'text-green-800 dark:text-green-300',
    },
    icon: 'shield',
  },
  {
    type: 'FIZETOS',
    labelKey: 'worksheet.repairType.fizetos',
    descriptionKey: 'worksheet.repairType.fizetosDesc',
    code: 'ML-J',
    color: {
      ring: 'ring-blue-500 bg-blue-50 dark:bg-blue-900/20',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      icon: 'text-blue-600 dark:text-blue-400',
      badge: 'bg-blue-100 dark:bg-blue-900/30',
      badgeText: 'text-blue-800 dark:text-blue-300',
    },
    icon: 'wrench',
  },
  {
    type: 'SZERZODESES',
    labelKey: 'worksheet.repairType.szerzodeses',
    descriptionKey: 'worksheet.repairType.szerzodesesDesc',
    code: 'ML-J-P',
    color: {
      ring: 'ring-purple-500 bg-purple-50 dark:bg-purple-900/20',
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      icon: 'text-purple-600 dark:text-purple-400',
      badge: 'bg-purple-100 dark:bg-purple-900/30',
      badgeText: 'text-purple-800 dark:text-purple-300',
    },
    icon: 'handshake',
  },
];

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function HandshakeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

const ICON_MAP = {
  shield: ShieldIcon,
  wrench: WrenchIcon,
  handshake: HandshakeIcon,
} as const;

interface RepairTypeStepProps {
  suggestedType?: RepairType;
}

export function RepairTypeStep({ suggestedType }: RepairTypeStepProps) {
  const { t } = useTranslation();
  const { repairType, setRepairType } = useWorksheetWizardStore();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('worksheet.repairType.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('worksheet.repairType.description')}
        </p>
      </div>

      {/* Repair type cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {REPAIR_TYPE_OPTIONS.map(option => {
          const IconComponent = ICON_MAP[option.icon];
          const isSelected = repairType === option.type;
          const isSuggested = suggestedType === option.type;

          return (
            <Card
              key={option.type}
              className={cn(
                'relative cursor-pointer p-5 transition-all hover:shadow-md border-2',
                isSelected
                  ? `ring-2 ${option.color.ring} shadow-lg`
                  : 'border-transparent hover:border-gray-300 dark:hover:border-[#3a9aa8]'
              )}
              onClick={() => setRepairType(option.type)}
            >
              {/* Suggested badge */}
              {isSuggested && (
                <div className="absolute -top-2 right-2">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      option.color.badge,
                      option.color.badgeText
                    )}
                  >
                    {t('worksheet.repairType.szerzodesesBadge')}
                  </span>
                </div>
              )}

              <div className="flex flex-col items-center gap-3 text-center">
                {/* Icon */}
                <div
                  className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-full',
                    option.color.bg
                  )}
                >
                  <IconComponent className={cn('h-6 w-6', option.color.icon)} />
                </div>

                {/* Label */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t(option.labelKey)}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-[#ccfbf1]">{option.code}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
                    {t(option.descriptionKey)}
                  </p>
                </div>

                {/* Selection indicator */}
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-gray-300 dark:border-[#2d7d8a]'
                  )}
                >
                  {isSelected && (
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Selected type summary */}
      {repairType && (
        <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-green-800 dark:text-green-300">
              {t('worksheet.repairType.selected', {
                type: t(
                  REPAIR_TYPE_OPTIONS.find(o => o.type === repairType)?.labelKey ?? repairType
                ),
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
